import React, { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useFlags, useFlagsmith } from 'flagsmith/react';
import { debounce } from 'lodash';

import { FeatureFlag } from 'app/api/services/site-configs/types';

import { HttpApiServiceContext } from 'app/api';
import { LanguageStore, useTranslation } from 'app/lang';
import { getFreebets } from 'app/lib/account/actions';
import { selectClientInfoData, selectClientProfilePhone } from 'app/lib/account/selectors';
import { useFeatureFlag, useFeatureFlagsInit } from 'app/lib/feature-flags';
import FreshchatProvider from 'app/lib/freshchat';
import { freshchatConfig } from 'app/lib/freshchat/freshchat-config';
import usePrevious from 'app/lib/hooks/usePrevious';
import { setWindowSize } from 'app/lib/screen/actions';
import { useYandexMetrika } from 'app/lib/yandex-metrika/useYandexMetrika';
import { setMatchesContainerHeight } from 'app/modules/Prematch/actions';
import { useAnalytics, useAuthorizedInfo } from 'app/packages/analytics';
import { getAbTestInfo } from 'app/packages/analytics/utils/getAbTestInfo';
import { isSSR } from 'app/packages/components';
import { sentrySetUser } from 'app/packages/components/Sentry';
import { cookie, hooks, mobile } from 'app/packages/lib';
import { getIsLoginSuccess, getIsLogoutSuccess, selectIsLoggedIn } from 'app/redux/selectors/auth';
import { allowGoogleTagManager, GUEST_ID_COOKIE_NAME } from 'app/ssr/config';
import { CookieConsent } from 'app/widgets/CookieConsent';

import { getUniversalEnv } from './lib/get-universal-env';
import { ROUTE_PATH } from './lib/routing/constants';
import { fetchStakeNamesTemplateByChangeLang } from './lib/stake-templates';
import Layout from './modules/Layout';
import { pushDataToGtmFromDataset } from './packages/analytics/utils/push-data-ga-from-dataset';
import { getBrandSEO } from './packages/brand';
import { loginAfterRedirect } from './redux/actions/auth';
import { getBackendServerUrl } from './ssr/utils';
import { DownloadModal } from './widgets/DownloadModal';
import { PromocodeModal } from './widgets/PromocodeModal';

import styles from './App.module.scss';

export const DEFAULT_TITLE = 'Букмекерская контора';

const YANDEXMETRIKA_ID = !isSSR && window.__env.YANDEXMETRIKA_ID;

function App({ query, createHttpApiService, children }) {
  const { t, lang } = useTranslation();
  const flagsmith  = useFlagsmith();
  const clientInfoData = useSelector(selectClientInfoData);
  const clientProfilePhone = useSelector(selectClientProfilePhone);
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const isLoginSuccess = useSelector(getIsLoginSuccess);
  const isLogoutSuccess = useSelector(getIsLogoutSuccess);
  const { pathname, search } = useLocation();
  const previousPathname = usePrevious(pathname);
  const dispatch = useDispatch();
  const mobileWebAppURL = getUniversalEnv('MOBILE_WEB_APP_URL');
  const instructionsURL = `${getBackendServerUrl()}${ROUTE_PATH.DOWNLOAD}`;

  const guestId = !isSSR ? cookie.getCookieByKey(GUEST_ID_COOKIE_NAME) : undefined;

  sentrySetUser({ clientId: clientInfoData?.clientId });

  useFeatureFlagsInit({ query, config: { identity: clientProfilePhone || guestId } });

  const isAllowSupport = useFeatureFlag(FeatureFlag.ALLOW_SUPPORT);
  const isCookieConsent = useFeatureFlag(FeatureFlag.COOKIE_CONSENT);
  const isQRtoInstructions = useFeatureFlag(FeatureFlag.QR_TO_INSTRUCTIONS);

  const flags = flagsmith?.getAllFlags();

  const abTestInfo = useMemo(() => getAbTestInfo(flags), [flags]);

  useAnalytics({
    active: allowGoogleTagManager,
    additionalDataForGA: {
      ...useAuthorizedInfo({ userId: clientInfoData?.clientId || guestId }),
      ...abTestInfo,
    },
  });

  useEffect(() => {
    if (search && search.includes('redirectKey')) {
      const redirectKey = search.split('=')[1];
      dispatch(loginAfterRedirect({ redirectKey }));
    }
  }, []);

  useEffect(() => {
    const updateSize = debounce(
      () => dispatch(setWindowSize(window.innerWidth, window.innerHeight)),
      300,
    );

    window.addEventListener('resize', updateSize);
    updateSize();

    return () => {
      window.removeEventListener('resize', updateSize);
    };
  }, []);

  useEffect(() => {
    if (!pathname.includes('prematch') && previousPathname.includes('prematch')) {
      dispatch(setMatchesContainerHeight(null));
    }
  }, [pathname]);

  useYandexMetrika({
    id: YANDEXMETRIKA_ID,
    isLoginSuccess,
    isLogoutSuccess,
  });

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(getFreebets());
    }
  }, []);

  const initialUserData = clientInfoData && {
    clientId: clientInfoData.clientId,
    restoreId: clientInfoData.freshchat.restoreId || null,
    externalId: clientInfoData.clientId,
  };

  useEffect(() => {
    !isSSR && LanguageStore.instance.on('languageChanged', fetchStakeNamesTemplateByChangeLang);

    return () => {
      !isSSR && LanguageStore.instance.off('languageChanged', fetchStakeNamesTemplateByChangeLang);
    };
  }, []);

  return (
    <div className={styles.appRoot}>
      {getBrandSEO()}

      <HttpApiServiceContext.Provider value={createHttpApiService}>
        {isAllowSupport ? (
          <FreshchatProvider {...freshchatConfig}
                             initialUserData={initialUserData}>
            <Layout>{children}</Layout>
          </FreshchatProvider>
        ) : (
          <Layout>
            {children}
          </Layout>
        )}
      </HttpApiServiceContext.Provider>
      <DownloadModal host={isQRtoInstructions ? instructionsURL : mobileWebAppURL} />
      {isCookieConsent && <CookieConsent />}
      <PromocodeModal />
    </div>
  );
}

export default App;
